import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, TextInput } from '@bravemove/ui-web';
import { CompassWordmark } from '@bravemove/design-tokens/brand';
import { adminFetch } from '../auth/apiClient';
import { AuthLayout } from '../layout/AuthLayout';

interface FormValues {
  readonly email: string;
}

export function ForgotPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);
  const schema = z.object({
    email: z.string().email(t('admin:validation.email')),
  });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    // Endpoint always returns 204; swallow any network failure quietly.
    try {
      await adminFetch('/admin/auth/forgot-password', {
        method: 'POST',
        body: { email: values.email },
      });
    } catch {
      // Intentionally ignored — UI promises a generic confirmation either way.
    }
    setSent(true);
  });

  return (
    <AuthLayout>
      <header className="text-center mb-6">
        <CompassWordmark size={28} />
        <h1 className="text-h2 font-bold text-ink mt-4">{t('admin:forgotPassword.title')}</h1>
        <p className="text-body text-ink-2 mt-1">{t('admin:forgotPassword.subtitle')}</p>
      </header>
      {sent ? (
        <div className="space-y-4">
          <Banner tone="success" title={t('admin:forgotPassword.sentTitle')}>
            {t('admin:forgotPassword.sentBody')}
          </Banner>
          <Button type="button" variant="secondary" fullWidth onClick={() => navigate('/login')}>
            {t('admin:forgotPassword.backToLogin')}
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            label={t('admin:login.emailLabel')}
            error={form.formState.errors.email?.message}
            required
          >
            <TextInput
              type="email"
              autoComplete="username"
              placeholder={t('admin:login.emailPlaceholder')}
              {...form.register('email')}
            />
          </FormField>
          <Button type="submit" variant="primary" fullWidth loading={form.formState.isSubmitting}>
            {t('admin:forgotPassword.submit')}
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => navigate('/login')}
            className="block mx-auto"
          >
            {t('admin:forgotPassword.backToLogin')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
